import { useState } from "react";
import { ChevronDown, Copy, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/components/ui/use-toast";
import type { DiagnosisFeedbackRow, DiagnosisMessageRow } from "@/services/callDiagnosisService";
import { DiagnosisFeedback } from "./DiagnosisFeedback";
import { SimpleMarkdown } from "./SimpleMarkdown";
import { buildIssueMarkdown, type IssueMarkdownInput } from "./issueMarkdown";

interface Props {
  message: DiagnosisMessageRow;
  // the user question this answers, for the GitHub issue text
  question: string;
  issueContext: Omit<IssueMarkdownInput, "question" | "answer">;
  currentUserId: string | null;
  onFeedbackChange?: (messageId: string, rows: DiagnosisFeedbackRow[]) => void;
}

// "## Verdict" first, everything after it (Timeline, Evidence, Likely fix) collapsible.
export function splitAnswer(text: string): { verdict: string; rest: string } {
  const match = text.match(/(^|\n)##\s*Verdict[^\n]*\n([\s\S]*?)(?=\n##\s|$)/i);
  if (!match) return { verdict: text, rest: "" };
  const end = (match.index ?? 0) + match[0].length;
  return { verdict: match[2].trim(), rest: text.slice(end).trim() };
}

const compact = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

// Same cut-off as diagnose_call: a row still `running` after this belongs to a run that died.
const STALE_RUNNING_MS = 5 * 60_000;

export function DiagnosisMessage({ message: row, question, issueContext, currentUserId, onFeedbackChange }: Props) {
  const stale =
    row.status === "running" && !row.id.startsWith("tmp-") && Date.now() - new Date(row.created_at).getTime() >= STALE_RUNNING_MS;
  const message = stale ? { ...row, status: "error" as const, error: row.error ?? "This run did not finish." } : row;
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const running = message.status === "running";
  const text = message.text ?? "";
  const { verdict, rest } = running ? { verdict: text, rest: "" } : splitAnswer(text);

  const copyIssue = async () => {
    try {
      await navigator.clipboard.writeText(buildIssueMarkdown({ ...issueContext, question, answer: text }));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Copied", description: "GitHub issue text is on your clipboard." });
    } catch {
      toast({ title: "Copy failed", description: "Clipboard is not available.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-3 rounded-lg border bg-card p-3">
      {message.steps.length > 0 && (
        <ul className="space-y-1">
          {message.steps.map((s, i) => {
            const last = i === message.steps.length - 1;
            return (
              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                {running && last ? (
                  <Loader2 className="mt-0.5 h-3 w-3 shrink-0 animate-spin" />
                ) : (
                  <Check className="mt-0.5 h-3 w-3 shrink-0 text-good" />
                )}
                <span>
                  {s.label}
                  {s.detail ? <span className="opacity-70"> · {s.detail}</span> : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {running && !text && message.steps.length === 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Starting...
        </div>
      )}

      {text && (
        <div className={running ? "" : "rounded-md border-l-4 border-primary bg-primary/5 p-2"}>
          {!running && <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">Verdict</div>}
          <SimpleMarkdown text={verdict} />
        </div>
      )}

      {rest && (
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
              <ChevronDown className={`mr-1 h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
              {open ? "Hide" : "Show"} timeline, evidence and likely fix
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <SimpleMarkdown text={rest} />
          </CollapsibleContent>
        </Collapsible>
      )}

      {message.status === "error" && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
          {message.error || "The diagnosis failed."} Send the question again to retry.
        </div>
      )}

      {message.status === "done" && (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {compact(message.input_tokens)} in · {compact(message.output_tokens)} out · $
              {Number(message.cost_usd).toFixed(3)}
            </span>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={copyIssue}>
              {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
              Copy as GitHub issue
            </Button>
          </div>
          <DiagnosisFeedback
            messageId={message.id}
            feedback={message.feedback ?? []}
            currentUserId={currentUserId}
            onChange={(rows) => onFeedbackChange?.(message.id, rows)}
          />
        </>
      )}
    </div>
  );
}
